import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';

/**
 * Re-exported from `packages/shared`, where the keys live so the SSR data resolver and
 * this cache cannot disagree about them (see `shared/query-keys.ts`).
 */
export { queryKeys } from '@nk/shared';

/**
 * TanStack Query configuration — phases.md Phase 7 ("sensible `staleTime` per resource").
 *
 * The `staleTime`s below mirror §1.11's cache policy rather than inventing a second one:
 * if the CDN may serve a homepage for 60 seconds, a tab that has been open for 30 has no
 * reason to refetch it. Realtime surfaces are the exception and are marked stale
 * immediately, because they have an SSE channel that will tell them when to change.
 */
export const STALE = {
  /** Matches the homepage aggregate's own `s-maxage`. */
  homepage: 60_000,
  feed: 60_000,
  /** Article pages are `s-maxage=300`; a published correction reaches readers in ≤5 min. */
  article: 300_000,
  /** Taxonomy changes when an editor adds a category — rare, and never urgent. */
  taxonomy: 15 * 60_000,
  search: 30_000,
  /** §1.11 `no-store`: always refetch, and let SSE push the changes in between. */
  realtime: 0,
} as const;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE.feed,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        /**
         * Retrying a 404 or a 400 is pure latency for the reader — the answer will not
         * change. Only 5xx and network failures are worth a second attempt, and only one.
         */
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 1;
        },
      },
    },
  });
}
